// Models
import { IAthleteInfoState } from 'storage/athleteInfo/models'
import { IExerciseData } from 'storage/exercise/models'
import { IFormInputs } from 'models'
import { IWorkoutModelState } from 'storage/workoutModel/models'
import {
  IWorkoutRoutine,
  IWorkoutRoutineState,
} from 'storage/workoutRoutine/models'
import IStore from 'lib/redux/models'

// React
import { FC, useContext, useLayoutEffect, useState } from 'react'

// Libraries
import { FormProvider, useForm } from 'react-hook-form'
import { ThemeContext } from 'styled-components'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { yupResolver } from '@hookform/resolvers/yup'

// Misc
import { cleanup, triggerLoadAthlete } from 'storage/athleteInfo/duck'
import { prepareModelsToPayload } from 'filters/workouts'
import {
  success as successModel,
  triggerCreateWorkoutsModel,
  triggerDeleteWorkoutsModel,
  triggerListWorkoutsModel,
  triggerUpdateWorkoutsModel,
} from 'storage/workoutModel/duck'
import { triggerLoadAthletePlans } from 'storage/athletePlans/duck'
import { urls } from 'routes/paths'
import { useModal } from 'hooks'
import { workoutModelsSchema } from 'schemas'
import useMediaQuery from 'hooks/useMediaQuery'

// Components
import * as Styled from './styled'
import { Loading } from 'heeds-ds'
import { ModalDelete, ModalExerciseImage } from 'components'
import Desktop from './desktop'
import Mobile from './mobile'

// Assets

const WorkoutReview: FC = () => {
  const { id = '', routine_id = '' } = useParams()
  const { loading } = useSelector<IStore, IAthleteInfoState>(
    (state) => state.athleteInfo,
  )
  const { models, workoutModels } = useSelector<IStore, IWorkoutModelState>(
    (state) => state.workoutModel,
  )
  const { workoutRoutine } = useSelector<IStore, IWorkoutRoutineState>(
    (state) => state.workoutRoutine,
  )
  const { closeModal, openModal, toggleModal, isVisible } = useModal()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useContext(ThemeContext)
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.tablet}px)`)

  const [selectedSet, setSelectedSet] = useState('')
  const [selectedExercise, setSelectedExercise] = useState<IExerciseData>()

  const methods = useForm<IFormInputs>({
    resolver: yupResolver(workoutModelsSchema),
    delayError: 800,
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { models: workoutModels || [] },
  })

  const toggleShowModal = () => {
    toggleModal('delete-exercise-modal')
  }

  const handleShowModal = (setId: string) => {
    setSelectedSet(setId)
    toggleShowModal()
  }

  const handleDelete = () => {
    if (workoutModels) {
      const updatedModels = workoutModels.map((stateModel, index) => {
        return {
          ...stateModel,
          workout_set: Object.values(stateModel.workout_set).reduce(
            (acc, set) => {
              if (set.id !== selectedSet) return { ...acc, [set.id]: set }
              methods.unregister(`models.${index}.workout_set.${set.id}`)
              return acc
            },
            {},
          ),
        }
      })

      dispatch(successModel({ workoutModels: updatedModels }))
    }
    toggleShowModal()
  }

  const getFilter = (
    routine: IWorkoutRoutine,
  ): 'current' | 'history' | 'scheduled' => {
    const { start_date, end_date } = routine
    const now = new Date()

    if (!start_date) {
      if (!end_date) return 'current'

      const end = new Date(end_date)

      if (end < now) return 'history'
      return 'current'
    }

    const start = new Date(start_date)

    if (!end_date) {
      if (start > now) return 'scheduled'
      return 'current'
    }

    const end = new Date(end_date)

    if (end < now) return 'history'
    if (start > now) return 'scheduled'
    return 'current'
  }

  const openExerciseGifModal = (exercise: IExerciseData) => {
    setSelectedExercise(exercise)
    openModal('mobile-exercise-gif-modal')
  }

  const onSubmit = (dataForm: IFormInputs) => {
    if (workoutModels && workoutRoutine) {
      let changed = false
      const updatedData = prepareModelsToPayload(dataForm.models)

      updatedData.forEach((workoutModel) => {
        if (workoutModel.id) {
          changed = true
          dispatch(
            triggerUpdateWorkoutsModel({
              ...workoutModel,
              model_pk: workoutModel.id,
              workout_routine: workoutRoutine.id,
            }),
          )
        } else {
          dispatch(
            triggerCreateWorkoutsModel({
              ...workoutModel,
              routine_pk: workoutRoutine.id,
            }),
          )
        }
      })

      models?.results.forEach((model) => {
        const index = workoutModels.findIndex(
          (stateModel) => stateModel.id === `${model.id}`,
        )
        if (index === -1) {
          dispatch(triggerDeleteWorkoutsModel({ model_pk: model.id }))
        }
      })

      dispatch(triggerListWorkoutsModel({ routine_pk: workoutRoutine.id }))

      const filter = getFilter(workoutRoutine)

      // TODO: rever esse fluxo, e error handling
      window.postMessage('CREATION_SUCCESS')
      if (changed) {
        navigate(generatePath(urls.workoutRoutine, { id, routine_id }))
      } else {
        navigate({
          pathname: generatePath(urls.athleteRoutines, { id }),
          search: `?routine_status=${filter}`,
        })
      }
    }
  }

  const reviewProps = {
    openExerciseGifModal,
    removeWorkoutSet: handleShowModal,
    workoutData: workoutModels,
  }

  useLayoutEffect(() => {
    dispatch(cleanup())
    if (id) {
      dispatch(triggerLoadAthlete({ id: Number(id) }))
      dispatch(triggerLoadAthletePlans({ id: Number(id) }))
    }
  }, [dispatch, id])

  if (loading) {
    return <Loading active />
  }

  return (
    <Styled.Container>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          {isDesktop ? (
            <Desktop {...reviewProps} />
          ) : (
            <Mobile {...reviewProps} />
          )}
        </form>
      </FormProvider>

      {isVisible === 'mobile-exercise-gif-modal' && (
        <ModalExerciseImage
          exercise={selectedExercise}
          handleClose={closeModal}
        />
      )}

      {isVisible === 'delete-exercise-modal' && (
        <ModalDelete
          description={'Tem certeza que deseja excluir este exercício?'}
          onConfirm={() => handleDelete()}
          title={'Excluir Exercício'}
          confirmTitle={'Excluir'}
        />
      )}
    </Styled.Container>
  )
}

export default WorkoutReview
